import TaxiCorporationDriverPlanResponse, {
  TaxiCorporationDriverPlanResponseDataFullPlanEntryCheckoutEvent,
  TaxiCorporationDriverPlanResponseDataFullPlanEntryEndNode,
  TaxiCorporationDriverPlanResponseDataFullPlanEntryEndNodeMeta,
  TaxiCorporationDriverPlanResponseDataFullPlanEntryNodeExclusion,
  TaxiCorporationDriverPlanResponseDataFullPlanEntryNodeExclusions,
  TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrders,
  TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRide,
  TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRideSeq,
  TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRideSeqMeta,
  TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersTransportingOrder,
  TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersTransportingOrderCargoOrder,
  TaxiCorporationDriverPlanResponseDataFullPlanEntryStartNode,
  TaxiCorporationDriverPlanResponseDataFullPlanEntryStartNodeMeta,
  TaxiCorporationDriverPlanResponseDataPlanEntry,
  TaxiCorporationDriverPlanResponseDataPlanEntryViewType,
  TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryEndNode,
  TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryEndNodeMeta,
  TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryStartNode,
  TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryStartNodeMeta,
} from "../../../../api/raily/taxi-corporation/driver/plan/taxi-corporation-driver-plan.response";
import PlanEntryType from "../../../../types/plan-entry-type";
import TaxiCorporationDriverPlan, {
  TaxiCorporationDriverPlanEntry,
  TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent,
  TaxiCorporationDriverPlanFullPlanEntryEndNode,
  TaxiCorporationDriverPlanFullPlanEntryEndNodeMeta,
  TaxiCorporationDriverPlanFullPlanEntryNodeExclusion,
  TaxiCorporationDriverPlanFullPlanEntryNodeExclusions,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrders,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRide,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRideSeq,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRideSeqMeta,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersTransportingOrder,
  TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersTransportingOrderCargoOrder,
  TaxiCorporationDriverPlanFullPlanEntryStartNode,
  TaxiCorporationDriverPlanFullPlanEntryStartNodeMeta,
  TaxiCorporationDriverPlanRestrictedPlanEntryEndNode,
  TaxiCorporationDriverPlanRestrictedPlanEntryEndNodeMeta,
  TaxiCorporationDriverPlanRestrictedPlanEntryStartNode,
  TaxiCorporationDriverPlanRestrictedPlanEntryStartNodeMeta,
} from "./taxi-corporation-driver-plan";

const createEntryFullPlanEntryCheckoutEvent = (
  checkoutEvent: TaxiCorporationDriverPlanResponseDataFullPlanEntryCheckoutEvent,
): TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent => {
  return {
    achievedAt: checkoutEvent.achievedAt,
    createdAt: checkoutEvent.createdAt,
    createdByName: checkoutEvent.createdByName,
    haltingTime: checkoutEvent.haltingTime,
    highwayCharge: checkoutEvent.highwayCharge,
    id: checkoutEvent.id,
    nodeId: checkoutEvent.nodeId,
  };
};

const createEntryFullPlanEntryCheckoutEvents = (
  checkoutEvents: TaxiCorporationDriverPlanResponseDataFullPlanEntryCheckoutEvent[],
): TaxiCorporationDriverPlanFullPlanEntryCheckoutEvent[] => {
  return checkoutEvents.map(createEntryFullPlanEntryCheckoutEvent);
};

const createEntryFullPlanEntryEndNodeMeta = (
  meta: TaxiCorporationDriverPlanResponseDataFullPlanEntryEndNodeMeta,
): TaxiCorporationDriverPlanFullPlanEntryEndNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryFullPlanEntryEndNode = (
  endNode: TaxiCorporationDriverPlanResponseDataFullPlanEntryEndNode,
): TaxiCorporationDriverPlanFullPlanEntryEndNode => {
  return {
    displayName: endNode.displayName,
    haltingTime: endNode.haltingTime,
    lat: endNode.lat,
    lon: endNode.lon,
    meta: createEntryFullPlanEntryEndNodeMeta(endNode.meta),
    time: endNode.time,
  };
};

const createEntryFullPlanEntryStartNodeMeta = (
  meta: TaxiCorporationDriverPlanResponseDataFullPlanEntryStartNodeMeta,
): TaxiCorporationDriverPlanFullPlanEntryStartNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryFullPlanEntryStartNode = (
  startNode: TaxiCorporationDriverPlanResponseDataFullPlanEntryStartNode,
): TaxiCorporationDriverPlanFullPlanEntryStartNode => {
  return {
    displayName: startNode.displayName,
    haltingTime: startNode.haltingTime,
    lat: startNode.lat,
    lon: startNode.lon,
    meta: createEntryFullPlanEntryStartNodeMeta(startNode.meta),
    time: startNode.time,
  };
};

const createEntryFullPlanEntrySolvedOrderRideSeqMeta = (
  meta: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRideSeqMeta,
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRideSeqMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryFullPlanEntrySolvedOrderRideSeqItem = (
  item: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRideSeq,
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRideSeq => {
  return {
    displayName: item.displayName,
    haltingTime: item.haltingTime,
    lat: item.lat,
    lon: item.lon,
    meta: createEntryFullPlanEntrySolvedOrderRideSeqMeta(item.meta),
    time: item.time,
  };
};

const createEntryFullPlanEntrySolvedOrderRideSeq = (
  seq: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRideSeq[],
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRideSeq[] => {
  return seq.map(createEntryFullPlanEntrySolvedOrderRideSeqItem);
};

const createEntryFullPlanEntrySolvedOrderRide = (
  ride: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersRide,
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersRide => {
  return {
    seq: createEntryFullPlanEntrySolvedOrderRideSeq(ride.seq),
  };
};

const createEntryFullPlanEntrySolvedOrderTransportingOrderCargoOrder = (
  cargoOrder: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersTransportingOrderCargoOrder,
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersTransportingOrderCargoOrder => {
  return {
    humanId: cargoOrder.humanId,
    id: cargoOrder.id,
  };
};

const createEntryFullPlanEntrySolvedOrderTransportingOrder = (
  transportingOrder: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrdersTransportingOrder,
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrdersTransportingOrder => {
  return {
    cargoOrder: createEntryFullPlanEntrySolvedOrderTransportingOrderCargoOrder(
      transportingOrder.cargoOrder,
    ),
  };
};

const createEntryFullPlanEntrySolvedOrder = (
  solvedOrder: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrders,
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrders => {
  return {
    id: solvedOrder.id,
    ride: createEntryFullPlanEntrySolvedOrderRide(solvedOrder.ride),
    transportingOrder: createEntryFullPlanEntrySolvedOrderTransportingOrder(
      solvedOrder.transportingOrder,
    ),
  };
};

const createEntryFullPlanEntrySolvedOrders = (
  solvedOrders: TaxiCorporationDriverPlanResponseDataFullPlanEntrySolvedOrders[],
): TaxiCorporationDriverPlanFullPlanEntrySolvedOrders[] => {
  return solvedOrders.map(createEntryFullPlanEntrySolvedOrder);
};

const createEntryFullPlanEntryNodeExclusion = (
  nodeExclusion: TaxiCorporationDriverPlanResponseDataFullPlanEntryNodeExclusion,
): TaxiCorporationDriverPlanFullPlanEntryNodeExclusion => {
  return {
    excludeApproaching: nodeExclusion.excludeApproaching,
    excludeInside: nodeExclusion.excludeInside,
    excludeReturning: nodeExclusion.excludeReturning,
    nodeIds: nodeExclusion.nodeIds,
  };
};

const createEntryFullPlanEntryNodeExclusions = (
  nodeExclusions: TaxiCorporationDriverPlanResponseDataFullPlanEntryNodeExclusion[],
): TaxiCorporationDriverPlanFullPlanEntryNodeExclusion[] => {
  return nodeExclusions.map(createEntryFullPlanEntryNodeExclusion);
};

const createEntryFullPlanEntryNode = (
  node: TaxiCorporationDriverPlanResponseDataFullPlanEntryNodeExclusions,
): TaxiCorporationDriverPlanFullPlanEntryNodeExclusions => {
  return {
    exclusions: createEntryFullPlanEntryNodeExclusions(node.exclusions),
  };
};

const createEntryRestrictedPlanEntryEndNodeMeta = (
  meta: TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryEndNodeMeta,
): TaxiCorporationDriverPlanRestrictedPlanEntryEndNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryRestrictedPlanEntryEndNode = (
  endNode: TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryEndNode,
): TaxiCorporationDriverPlanRestrictedPlanEntryEndNode => {
  return {
    displayName: endNode.displayName,
    haltingTime: endNode.haltingTime,
    lat: endNode.lat,
    lon: endNode.lon,
    meta: createEntryRestrictedPlanEntryEndNodeMeta(endNode.meta),
    time: endNode.time,
  };
};

const createEntryRestrictedPlanEntryStartNodeMeta = (
  meta: TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryStartNodeMeta,
): TaxiCorporationDriverPlanRestrictedPlanEntryStartNodeMeta => {
  return {
    estimatedTime: meta.estimatedTime,
    id: meta.id,
  };
};

const createEntryRestrictedPlanEntryStartNode = (
  startNode: TaxiCorporationDriverPlanResponseDataRestrictedPlanEntryStartNode,
): TaxiCorporationDriverPlanRestrictedPlanEntryStartNode => {
  return {
    displayName: startNode.displayName,
    haltingTime: startNode.haltingTime,
    lat: startNode.lat,
    lon: startNode.lon,
    meta: createEntryRestrictedPlanEntryStartNodeMeta(startNode.meta),
    time: startNode.time,
  };
};

const createEntry = (
  entry: TaxiCorporationDriverPlanResponseDataPlanEntry,
): TaxiCorporationDriverPlanEntry => {
  switch (entry.viewType) {
    case TaxiCorporationDriverPlanResponseDataPlanEntryViewType.FULL:
      return {
        checkoutEvents: createEntryFullPlanEntryCheckoutEvents(
          entry.checkoutEvents,
        ),
        endNode: createEntryFullPlanEntryEndNode(entry.endNode),
        humanId: entry.humanId,
        id: entry.id,
        nodeExclusions: createEntryFullPlanEntryNode(entry.nodeExclusions),
        solvedOrders: createEntryFullPlanEntrySolvedOrders(entry.solvedOrders),
        startNode: createEntryFullPlanEntryStartNode(entry.startNode),
        viewType: PlanEntryType.FULL,
      };
    case TaxiCorporationDriverPlanResponseDataPlanEntryViewType.RESTRICTED:
      return {
        id: entry.id,
        humanId: entry.humanId,
        startNode: createEntryRestrictedPlanEntryStartNode(entry.startNode),
        endNode: createEntryRestrictedPlanEntryEndNode(entry.endNode),
        viewType: PlanEntryType.RESTRICTED,
      };
  }
};

const createEntries = (
  entries: TaxiCorporationDriverPlanResponseDataPlanEntry[],
): TaxiCorporationDriverPlanEntry[] => {
  return entries.map(createEntry);
};

const create = (
  response: TaxiCorporationDriverPlanResponse,
): TaxiCorporationDriverPlan => {
  return {
    entries: createEntries(response.data.entries),
  };
};

const taxiCorporationDriverPlanFactory = {
  create,
};

export default taxiCorporationDriverPlanFactory;
